/**
 * Optional post-enrichment step flags carried on the `useCase` object of the
 * enrichment/catalog-operations trigger requests. Tri-state: `undefined` falls
 * back to the server env default; `true`/`false` force the step on/off per job.
 * See `docs/enrichment-post-step-flags.md` and the backend UI contract.
 */
export interface EnrichmentPostStepFlags {
  /** `SEARCH_DESCRIPTORS`, POST stage 0. Mutually exclusive with
   *  `phraseDescriptionsEnabled` — a run generates one form of the copy, not both. */
  searchDescriptorsEnabled?: boolean;
  /** `PHRASE_DESCRIPTIONS`, POST stage 1. Mutually exclusive with
   *  `searchDescriptorsEnabled` — enabling one clears the other. */
  phraseDescriptionsEnabled?: boolean;
  /** `SCORING` at PRE. Scores the retailer's untouched product (`data_label:
   *  "original"`). SCORING is the only bi-phase step; PRE is rejected for the
   *  others, which is why pre/post are separate flags rather than a phase field. */
  scoringPreEnabled?: boolean;
  /** `SCORING` at POST. Scores the enrichment output (`data_label: "enhanced"`).
   *  Independent of the PRE flag — both on is the common case, since that is what
   *  produces a before/after lift. */
  scoringPostEnabled?: boolean;
}

// `gapAnalysisEnabled` is deliberately absent. `UseCaseConfig` accepts it, but gap
// analysis is computed **on demand**: the per-product read is read-through — a
// first open starts the agent and the result is cached with no TTL, with an
// explicit refresh endpoint to recompute. A run-level toggle would imply the run
// produces the data, when opening a product produces it regardless of the flag.
// See `docs/catalog-operations.md` → Gap analysis.

/** Flag keys in render order. Grouped by what they do rather than by stage —
 *  "these run after enrichment" is enough for most users. */
export const ENRICHMENT_POST_STEP_KEYS = [
  'searchDescriptorsEnabled',
  'phraseDescriptionsEnabled',
  'scoringPreEnabled',
  'scoringPostEnabled',
] as const satisfies readonly (keyof EnrichmentPostStepFlags)[];

/**
 * Enforces that search and phrase descriptors are mutually exclusive — a run
 * generates one form of the copy, not both. The UI clears the sibling when a
 * step is switched on, so this is a serialisation safety net: if a caller still
 * has both on, keep search (stage 0) and drop phrase as a deterministic
 * tie-break, so an invalid pair can never be sent whichever flow builds the payload.
 */
export function resolveEnrichmentFlagDependencies(
  flags: EnrichmentPostStepFlags,
): EnrichmentPostStepFlags {
  if (flags.searchDescriptorsEnabled && flags.phraseDescriptionsEnabled) {
    return { ...flags, phraseDescriptionsEnabled: false };
  }
  return flags;
}

/**
 * Drops flags that are `undefined` so trigger payloads only carry explicitly-set
 * booleans — keeps requests minimal and lets the server default apply otherwise.
 */
export function compactEnrichmentFlags(flags: EnrichmentPostStepFlags): EnrichmentPostStepFlags {
  // Dependency is resolved here too, not just in the UI — every flow funnels
  // through this before serialising, so no caller can bypass it.
  const resolved = resolveEnrichmentFlagDependencies(flags);
  const out: EnrichmentPostStepFlags = {};
  for (const key of ENRICHMENT_POST_STEP_KEYS) {
    if (resolved[key] !== undefined) out[key] = resolved[key];
  }
  return out;
}
